import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../components/custom-table/custom-table";
import AsterasubmissionModal from "../../../components/modals/asteraSubmission/asteraSubmission";
import TrackStatusTabbedModal from "../../../components/modals/asteraTrackStatus/trackStatusTabbedModal";
import DocViewerModal from "../../../components/modals/docViewerModal/docViewerModalNew";
import PushToSftpModal from "../../../components/modals/pushToSftpModal/pushToSftpModal";
import SftpDocModal from "../../../components/modals/sftpDocModal/sftpDocModal";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { useModalOpener } from "../../../hooks/useModalOpener";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { shouldOpenCmmModal } from "../../pharmaStpFile/table/utils/shouldOpenCmmModal";
import { useAsteraOncologyExternalTableData } from "../hooks/useAsteraOncologyExternalTableData";
import { useAsteraScreenshot } from "../hooks/useAsteraScreenshot";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import {
  AsteraOncologyExternalTableHeader,
  generateTableDataForAsteraOncologyExternal,
} from "../table/asteraOncologyExternalTableData";
import { isAsteraOutcomeViewDisabled } from "../table/utils/isAsteraOutcomeViewDisabled";
import { createAllFilterSections } from "../utils/createFilterSections";
import {
  areAllRowsOnPageSelected,
  deselectRowsOnCurrentPage,
  selectRowsOnCurrentPage,
  toggleCheckboxSelection,
} from "../utils/toggleCheckboxSelection";

const AsteraOncologyExternalOrdersTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();

  useModalOpener();

  const getDateRange = () => {
    // Date format: YYYYMMDD (e.g., "20251201")
    const today = new Date();
    const twoMonthsAgo = new Date(today);
    twoMonthsAgo.setMonth(today.getMonth() - 2);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    };

    const startDate = formatDate(twoMonthsAgo);
    const endDate = formatDate(today);

    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRange();
  const pageSize = 120;

  const [filterData, setFilterData] = useState<FilterSection[]>([]);

  const {
    asteraOncologyExternalState,
    setAsteraOncologyExternalState,
    tableData,
    setTableData,
  } = useAsteraOncologyExternalTableData();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setAsteraOncologyExternalState,
      setTableData,
      startDate,
      endDate,
      pageSize,
    );

  const { paginationResetCount, setSearchTerm, searchTerm, validFilters } =
    useFilterAndSearch(
      initialDataFetchingExecuted,
      setTableData,
      setAsteraOncologyExternalState,
      startDate,
      endDate,
      pageSize,
    );

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    asteraOncologyExternalState,
    setAsteraOncologyExternalState,
    setTableData,
    startDate,
    endDate,
    pageSize,
  );

  const [allOncologyOrdersTableData, setAllOncologyOrdersTableData] = useState<
    OncologySubmissionTableDataModel[]
  >([]);

  const [, setAreAllRowsSelected] = useState<boolean>(false);

  // Track current page indices for select all functionality
  const [currentPageStartIndex, setCurrentPageStartIndex] = useState<number>(0);
  const [currentPageEndIndex, setCurrentPageEndIndex] = useState<number>(9); // Default to itemsPerPage - 1

  const {
    asteraScreenshotDownloadUrl,
    asteraScreenshotModalOpen,
    selectedRowData: selectedAsteraRowData,
    handleViewClick,
    closeAsteraScreenshotModal,
    externalLoading: asteraScreenshotLoading,
  } = useAsteraScreenshot();

  useEffect(() => {
    const formattedTableData =
      generateTableDataForAsteraOncologyExternal(tableData);
    setAllOncologyOrdersTableData(formattedTableData);

    setAreAllRowsSelected(false);
  }, [tableData]);

  const handleToggleCheckboxSelection = useCallback((id: string) => {
    toggleCheckboxSelection(
      id,
      setAllOncologyOrdersTableData,
      () => {}, // no-op for setHasSelectedRows
      setAreAllRowsSelected,
    );
  }, []);

  // Handle select all / deselect all rows on current page only
  const handleSelectAll = useCallback(() => {
    const allOnPageSelected = areAllRowsOnPageSelected(
      allOncologyOrdersTableData,
      currentPageStartIndex,
      currentPageEndIndex,
    );

    if (allOnPageSelected) {
      // If all on current page are selected, deselect them
      deselectRowsOnCurrentPage(
        setAllOncologyOrdersTableData,
        currentPageStartIndex,
        currentPageEndIndex,
        setAreAllRowsSelected,
      );
    } else {
      // If not all on current page selected, select them
      selectRowsOnCurrentPage(
        setAllOncologyOrdersTableData,
        currentPageStartIndex,
        currentPageEndIndex,
        setAreAllRowsSelected,
      );
    }
  }, [allOncologyOrdersTableData, currentPageStartIndex, currentPageEndIndex]);

  // Open export confirmation modal

  // Execute export selected rows to CSV via API call

  const handleSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  const handleRefresh = () => {
    setInitialDataFetchingExecuted(false);
  };

  const handleViewCmmClick = (rowData: any) => {
    const responseStatus =
      rowData.response_status ?? rowData.responseStatusValue ?? null;
    if (isAsteraOutcomeViewDisabled(responseStatus)) {
      return;
    }
    if (shouldOpenCmmModal(rowData.cmmStatus)) {
      dispatch(
        setOpenedModalId({
          id: ModalId.SFTP_DOC_MODAL,
          metaData: {
            selectedRowData: rowData,
          },
        }),
      );
    }
  };

  const handleRowClick = (rowData: any) => {};

  const handleCloseSftpDocModal = () => {
    dispatch(closeModal());
  };

  const [selectedRowData, setSelectedRowData] =
    useState<OncologySubmissionTableDataModel | null>(null);

  const handleAddKeyButtonClick = (rowData: any) => {
    setSelectedRowData(rowData);
    dispatch(
      setOpenedModalId({
        id: ModalId.ASTERA_SUBMISSION_MODAL,
      }),
    );
  };

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const filters = createAllFilterSections(tableData);
      setFilterData(filters);
    }
  }, [tableData]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  // Watch for shouldRefetch flag from context
  const tableContextData = getTableDataFromContext(
    TableNames.ASTERA_ONCOLOGY_EXTERNAL_ORDERS,
  );

  useEffect(() => {
    if (tableContextData?.shouldRefetch) {
      // Trigger refetch
      setInitialDataFetchingExecuted(false);
      // Reset the flag
      setTableDataForContext(TableNames.ASTERA_ONCOLOGY_EXTERNAL_ORDERS, {
        shouldRefetch: false,
      });
    }
  }, [
    tableContextData?.shouldRefetch,
    setInitialDataFetchingExecuted,
    setTableDataForContext,
  ]);

  if (asteraOncologyExternalState.error) {
    console.error(
      "Error fetching oncology submission data:",
      asteraOncologyExternalState.error,
    );
  }

  const handleCloseAsterasubmissionModal = () => {
    dispatch(closeModal());
    setSelectedRowData(null);
  };

  const handleSaveAsterasubmission = () => {
    setInitialDataFetchingExecuted(false);
  };

  const handleAsteraTrackStatusSuccess = useCallback(() => {
    dispatch(closeModal());
    setInitialDataFetchingExecuted(false);
  }, [dispatch]);

  return (
    <>
      <CustomTable
        tableHeaders={AsteraOncologyExternalTableHeader}
        tableData={allOncologyOrdersTableData}
        itemsPerPage={10}
        pagesPerView={4}
        count={allOncologyOrdersTableData.length}
        totalCount={asteraOncologyExternalState.data?.total_count ?? 0}
        isFetching={asteraOncologyExternalState.loading}
        tableName={TableNames.ASTERA_ONCOLOGY_EXTERNAL_ORDERS}
        searchingText={handleSearch}
        placeholder="Search by RxNumber and Patient Name"
        onViewCmmClick={(data) => handleViewCmmClick(data)}
        // onTextNoteClick={(data) => handleTextNoteClick(data)}
        onRefreshButton={handleRefresh}
        onRowClick={(data) => handleRowClick(data)}
        isDisabled={asteraOncologyExternalState.loading}
        showRefreshButton={true}
        showOtherButton={true}
        showFilterButton={true}
        filterSectionsData={filterData}
        onFilterApplyClicked={handleFilterApply}
        endIndexOfTable={(endIndex) => {
          setEndIndex(endIndex);
          setCurrentPageEndIndex(endIndex);
        }}
        startIndexOfTable={setCurrentPageStartIndex}
        requestForPaginationReset={paginationResetCount}
        showInLineLoader={asteraOncologyExternalState.showInlineLoader}
        onAddKeyButtonClick={handleAddKeyButtonClick}
        onViewClick={handleViewClick}
        showButtonWithDropdown={false}
        toggleCheckboxSelection={handleToggleCheckboxSelection}
        isCheckboxSelected={areAllRowsOnPageSelected(
          allOncologyOrdersTableData,
          currentPageStartIndex,
          currentPageEndIndex,
        )}
        onSelectAll={handleSelectAll}
      />
      {openedModalId === ModalId.SFTP_DOC_MODAL && (
        <SftpDocModal onClose={handleCloseSftpDocModal} hideCallingSummaryTab />
      )}

      {openedModalId === ModalId.ASTERA_SUBMISSION_MODAL && (
        <AsterasubmissionModal
          metaData={{
            data: selectedRowData,
          }}
          onClose={handleCloseAsterasubmissionModal}
          onSuccess={handleSaveAsterasubmission}
        />
      )}
      {openedModalId === ModalId.ASTERA_TRACK_STATUS_MODAL && (
        <TrackStatusTabbedModal
          onClose={handleAsteraTrackStatusSuccess}
          onSuccess={handleAsteraTrackStatusSuccess}
        />
      )}
      {openedModalId === ModalId.PUSH_TO_SFTP_MODAL && <PushToSftpModal />}

      {asteraScreenshotModalOpen && selectedAsteraRowData && (
        <DocViewerModal
          fileUrl={asteraScreenshotDownloadUrl ?? ""}
          onClose={closeAsteraScreenshotModal}
          type="cmm_screenshot"
          externalLoading={asteraScreenshotLoading}
        />
      )}
    </>
  );
};

export default AsteraOncologyExternalOrdersTable;
